// A small Markdown renderer, shared by the editor's preview and the pages that
// render what was written. One implementation, so the preview cannot promise
// something the site renders differently.
//
// Written rather than installed. The subset here is bold, italic, inline code,
// links, headings, blockquotes, lists and rules, which is what the toolbar
// offers and what the writing needs; a general parser would be an order of
// magnitude more code for the rest of CommonMark that nothing here types.
//
// SAFETY. The output is handed to v-html, so it has to be safe by construction
// rather than by sanitising afterwards. Every character of input is HTML
// -escaped first, before any rule runs. After that there is no way for input to
// become a tag: `<script>` is already `&lt;script&gt;` by the time anything
// looks at it, and the only tags in the output are the ones written below.
// Nothing here ever interpolates input into an attribute except a link href,
// which is checked against an http(s) pattern.

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ESCAPES[c]);

// Runs on already-escaped text. Order matters: code first so its contents are
// left alone, then bold before italic, or `**a**` is eaten as two italics.
function inline(text) {
  return text
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_\n]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*([^*\n]+)\*(?![\w*])/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\n]+)_(?![\w_])/g, '$1<em>$2</em>')
    // Only http(s). The href is the one place input reaches an attribute, and
    // anything else there is how `javascript:` gets in.
    .replace(
      /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
}

const RULE = /^ {0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const HEADING = /^ {0,3}(#{1,3})\s+(.*)$/;
// `&gt;`, not `>`, because escaping runs before any of this and a quoted line
// therefore begins with the entity by the time it gets here. Matching `>` was
// the first version and silently rendered every blockquote as a paragraph
// starting with a visible &gt;.
const QUOTE = /^ {0,3}&gt;\s?(.*)$/;
const BULLET = /^ {0,3}[-*+]\s+(.*)$/;
const NUMBER = /^ {0,3}\d+[.)]\s+(.*)$/;

// Takes escaped text, returns HTML. Recurses for the inside of a blockquote,
// which is why it is separate from renderMarkdown: the escaping must happen
// exactly once, at the top.
function blocks(text) {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (RULE.test(line)) {
      out.push('<hr>');
      i += 1;
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      // Starts at h3: these render inside a page that already has its own h1
      // and h2, and a story part must not outrank them.
      const level = Math.min(heading[1].length + 2, 6);
      out.push(`<h${level}>${inline(heading[2].trim())}</h${level}>`);
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const buffer = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        buffer.push(lines[i].match(QUOTE)[1]);
        i += 1;
      }
      out.push(`<blockquote>${blocks(buffer.join('\n'))}</blockquote>`);
      continue;
    }

    if (BULLET.test(line) || NUMBER.test(line)) {
      const ordered = NUMBER.test(line);
      const pattern = ordered ? NUMBER : BULLET;
      const items = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push(`<li>${inline(lines[i].match(pattern)[1].trim())}</li>`);
        i += 1;
      }
      out.push(ordered ? `<ol>${items.join('')}</ol>` : `<ul>${items.join('')}</ul>`);
      continue;
    }

    // A paragraph runs until a blank line or the start of another block.
    const paragraph = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !RULE.test(lines[i]) &&
      !HEADING.test(lines[i]) &&
      !QUOTE.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !NUMBER.test(lines[i])
    ) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    // Single newlines inside a paragraph are kept as breaks. Prose written in
    // a textarea uses them to mean a line break, not to reflow.
    out.push(`<p>${inline(paragraph.join('<br>'))}</p>`);
  }

  return out.join('');
}

// Full block rendering, for long-form writing.
export function renderMarkdown(source) {
  if (!source) return '';
  return blocks(escapeHtml(source));
}

// Inline only, for the one-line fields: a card summary and an article excerpt
// are clamped to a few lines, so a heading or a blockquote there would be
// formatting the clamp then cuts in half. Bold, italic, code and links survive
// a clamp; blocks do not.
export function renderInline(source) {
  if (!source) return '';
  return inline(escapeHtml(source).replace(/\r?\n/g, ' '));
}
